import { motion } from "framer-motion";
import { useParams } from "react-router-dom";
import { useEffect, useState, useRef } from "react";

import { Agent } from "../../redux/reducers/agentSlice";
import { featureActions } from "../../redux/reducers/featureSlice";
import { InputField } from "../../components/base/InputField";
import { TelephonyConfigurationDropdown } from "../../components/profile/TelephonyConfigurationDropdown";
import { useAppDispatch, useAppSelector } from "../../redux";
import { useEffectiveOrganization } from "../../hooks/useEffectiveOrganization";
import { View } from "../../components/base";
import { AgentAnalytics } from "../../components/agents/AgentAnalytics";

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.4 },
  },
};

export const AgentDetailsPage = () => {
  const dispatch = useAppDispatch();
  const { agentId } = useParams<{ agentId: string }>();
  const agent = useAppSelector((state) => (agentId ? state.agent.entities[agentId] : null));
  const formData = useAppSelector((state) => state.feature.agentDetailsFormData);
  const { organizationId } = useEffectiveOrganization();
  const effectiveOrganizationId = organizationId || agent?.organizationId;
  const [selectedVoiceId, setSelectedVoiceId] = useState<string | null>(null);

  const [playingVoiceId, setPlayingVoiceId] = useState<string | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  const onChange = (updatedFormData: Partial<Agent>) => {
    dispatch(featureActions.setAgentDetailsFormData({ ...formData, ...updatedFormData }));
  };

  useEffect(() => {
    audioRef.current?.pause();
    setPlayingVoiceId(null);

    if (agent) {
      const initialData = { ...agent };
      dispatch(featureActions.setAgentDetailsFormData(initialData));
      setSelectedVoiceId(agent.defaultVoiceId || null);
    } else {
      const resetData: Partial<Agent> = { name: "", description: "", defaultVoiceId: undefined, telephonyId: null };
      dispatch(featureActions.setAgentDetailsFormData(resetData));
      setSelectedVoiceId(null);
    }
  }, [agent, agentId, dispatch]);

  useEffect(() => {
    return () => {
      audioRef.current?.pause();
    };
  }, []);

  const handleSelectVoice = (voiceId: string) => {
    setSelectedVoiceId(voiceId);
    onChange({ defaultVoiceId: voiceId });
  };

  const handlePlayPause = (voiceId: string, audioUrl: string) => {
    if (playingVoiceId === voiceId) {
      audioRef.current?.pause();
      setPlayingVoiceId(null);
    } else {
      audioRef.current?.pause();

      const newAudio = new Audio(audioUrl);
      audioRef.current = newAudio;
      newAudio.play();
      setPlayingVoiceId(voiceId);

      newAudio.addEventListener("ended", () => {
        if (audioRef.current === newAudio) {
          setPlayingVoiceId(null);
        }
      });
      newAudio.addEventListener("error", () => {
        if (audioRef.current === newAudio) {
          console.error("Error playing audio:", audioUrl);
          setPlayingVoiceId(null);
        }
      });
    }
  };

  return (
    <View className="relative flex flex-row h-full overflow-hidden">
      <View className="flex flex-col flex-1 py-2 h-full overflow-y-auto">
        <motion.div
          className="flex flex-col flex-1"
          variants={itemVariants}
          initial="hidden"
          animate="visible"
        >
          <View className="flex flex-col gap-6 flex-1 p-4">
            <AgentAnalytics />
          </View>
        </motion.div>
      </View>
    </View>
  );
};
